import { type FeedbackSurveyData } from '@aikoa/api'
import {
  contactLanguageSchema,
  getFeedbackQuestionnaireData,
  hasRequiredQuestionnaireAnswers,
  validatedPropertyInterestDataSchema,
  type PropertyInterestData,
  type QuestionnaireQuestionId,
} from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import z from 'zod/v4'
import { type ContactFormData } from '~/components/contacts/ContactForm'
import {
  buildInterestPayload,
  feedbackAnswersSchema,
  feedbackFlowDataSchema,
  feedbackFlowDataToQuestionnaireAnswers,
  type ContactForConsent,
  type FeedbackFlowData,
} from '~/components/feedback/feedbackFlowTypes'
import { buildQuestionnairePhases } from '~/components/feedback/feedbackPhaseBuilders'
import { FeedbackReviewPhase } from '~/components/feedback/FeedbackReviewPhase'
import { FlowProvider } from '~/components/flows/FlowProvider'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { useAlert } from '~/components/GlobalAlert'
import { LoadingView } from '~/components/LoadingView'
import { Button, ButtonLabel } from '~/components/ui/button'
import { InfoAlert } from '~/components/ui/info-alert'
import { useSession } from '~/components/user/SessionProvider'
import type { AddFeedbackPayload } from '~/data/feedback/types'
import {
  useSaveFeedbackProgress,
  useSetFeedbackInactive,
  useSubmitBuyerSearchUpdate,
  useSubmitFeedback,
  useSubmitRealtorLinkFeedback,
} from '~/data/feedback/useFeedback'
import { getOrCreateAnonymousUser } from '~/lib/auth/anonymous'
import { propertyFilterFormSchema } from '~/lib/propertyFormSchemas'
import {
  PropertyInterestDescription,
  PropertyInterestQuestion,
} from '../interests/FeedbackInterestQuestions'
import { QuestionnairePageRenderer } from '../questionnaires/FeedbackQuestionnaireComponents'

const realtorLinkContactSchema = z
  .object({
    name: z.string().min(2),
    email: z.string().optional(),
    phone: z.string().optional(),
    language: contactLanguageSchema.optional().default('fi'),
  })
  .refine((data) => !!data.email || !!data.phone)

interface FeedbackFlowProps {
  flowId: string
  onComplete: (outcome: 'completed' | 'inactive') => void
  onClose: () => void
  feedbackRequest?: FeedbackSurveyData
  realtorUserId?: string | null
  initialTimingAnswer?: number | null
  initialInterestData?: PropertyInterestData | null
  initialContactDetails?: ContactFormData
  flowTitle?: string
}

export function FeedbackFlow({
  flowId,
  onComplete,
  onClose,
  feedbackRequest,
  realtorUserId,
  initialTimingAnswer,
  initialInterestData,
  initialContactDetails,
  flowTitle,
}: FeedbackFlowProps) {
  const { t } = useTranslations('feedback')
  const { showAlert } = useAlert()
  const { session, isAnonymous, isLoading: isSessionLoading } = useSession()

  const submitFeedbackMutation = useSubmitFeedback()
  const submitBuyerSearchUpdateMutation = useSubmitBuyerSearchUpdate()
  const submitRealtorLinkFeedbackMutation = useSubmitRealtorLinkFeedback()
  const setFeedbackInactiveMutation = useSetFeedbackInactive()

  const [requiresConsentAuth, setRequiresConsentAuth] = useState<boolean | null>(null)

  const activeRealtorUserId = feedbackRequest?.realtor_user_id ?? realtorUserId ?? null

  useEffect(() => {
    if (isSessionLoading || requiresConsentAuth !== null) {
      return
    }

    setRequiresConsentAuth(!session?.user || isAnonymous)
  }, [isAnonymous, isSessionLoading, requiresConsentAuth, session?.user])

  const initialData = useMemo(() => {
    if (requiresConsentAuth === null) {
      return undefined
    }

    const nextInitialData: Partial<FeedbackFlowData> = {
      consentAuth: {
        requiresConsentAuth,
      },
    }

    if (initialContactDetails) {
      nextInitialData.contactDetails = {
        name: initialContactDetails.name,
        email: initialContactDetails.email,
        phone: initialContactDetails.phone,
        language: initialContactDetails.language,
      }
    }

    if (feedbackRequest?.interest?.answers && Array.isArray(feedbackRequest.interest.answers)) {
      const answersMap: Record<string, number | string | undefined> = {}

      for (const answer of feedbackRequest.interest.answers) {
        answersMap[answer.id] = answer.value
      }

      const parsedAnswers = feedbackAnswersSchema.safeParse(answersMap)

      if (parsedAnswers.success && Object.keys(parsedAnswers.data).length > 0) {
        nextInitialData.feedbackAnswers = parsedAnswers.data
      }
    }

    if (initialTimingAnswer !== null && initialTimingAnswer !== undefined) {
      nextInitialData.feedbackAnswers = {
        ...nextInitialData.feedbackAnswers,
        timing: initialTimingAnswer,
      } as FeedbackFlowData['feedbackAnswers']
    }

    return nextInitialData
  }, [feedbackRequest?.interest?.answers, initialContactDetails, initialTimingAnswer, requiresConsentAuth])

  const phases = useMemo(() => {
    if (requiresConsentAuth === null) {
      return []
    }

    return [
      ...buildQuestionnairePhases(t),
      {
        id: 'propertyInterest' as const,
        title: t(($) => $.interest.questionTitle),
        schema: validatedPropertyInterestDataSchema.pick({
          filters: true,
          locations: true,
        }),
        data: {
          filters: initialInterestData?.filters ?? {},
          locations: initialInterestData?.locations ?? [],
        },
        usesCustomButtons: true,
      },
      {
        id: 'interestDescription' as const,
        title: t(($) => $.interest.descriptionTitle),
        schema: validatedPropertyInterestDataSchema.pick({
          description: true,
        }),
        data: {
          description: initialInterestData?.description ?? null,
        },
        usesCustomButtons: true,
      },
      {
        id: 'review' as const,
        title: t(($) => $.review.title),
      },
      {
        id: 'consent' as const,
        title: t(($) => $.consent.title),
        usesCustomButtons: true,
        hiddenFromProgress: true,
        condition: (flowData: Partial<FeedbackFlowData>) => {
          const consentAuthData = flowData?.consentAuth

          if (consentAuthData) {
            return consentAuthData.requiresConsentAuth
          }

          return requiresConsentAuth ?? true
        },
      },
    ]
  }, [initialInterestData, requiresConsentAuth, t])

  const isNextDisabled = useCallback(
    (data: Partial<FeedbackFlowData>, currentPhaseId: string) => {
      if (currentPhaseId === 'propertyInterest') {
        const interestData = data?.propertyInterest as
          | { filters?: unknown; locations?: unknown }
          | undefined

        const structureValid = validatedPropertyInterestDataSchema
          .pick({ filters: true, locations: true })
          .safeParse(interestData).success

        if (!structureValid) {
          return true
        }

        return !propertyFilterFormSchema.safeParse(interestData?.filters).success
      }

      const questionnaireData = getFeedbackQuestionnaireData(t)
      const currentQuestion = questionnaireData.questions.find(
        (question) => question.id === currentPhaseId
      )

      if (!currentQuestion) {
        return false
      }

      const value = data?.feedbackAnswers?.[currentQuestion.id] as number | string | undefined

      if (currentQuestion.type === 'text-input') {
        return typeof value !== 'string' || value.trim().length === 0
      }

      if (currentQuestion.id === 'timing' && value === 0) {
        return true
      }

      return value === undefined
    },
    [t]
  )

  const handleSetInactive = useCallback(async () => {
    if (!feedbackRequest?.survey_nonce) {
      showAlert({
        type: 'error',
        title: t(($) => $.errors.title),
        description: t(($) => $.errors.invalidNonce),
      })
      return
    }

    try {
      const result = await setFeedbackInactiveMutation.mutateAsync({
        feedbackId: feedbackRequest.id,
        nonce: feedbackRequest.survey_nonce,
      })

      if (result.success) {
        onComplete('inactive')
        return
      }
    } catch (error) {
      console.error('Failed to set feedback contact inactive', error)
    }

    showAlert({
      type: 'error',
      title: t(($) => $.errors.title),
      description: t(($) => $.errors.submissionFailed),
    })
  }, [feedbackRequest, onComplete, setFeedbackInactiveMutation, showAlert, t])

  const handleFeedbackRequestSubmissionError = useCallback(
    (errorCode: string) => {
      if (errorCode === 'ALREADY_ANSWERED') {
        showAlert({
          type: 'info',
          title: t(($) => $.errors.alreadyAnswered),
          description: t(($) => $.errors.alreadyAnsweredDescription),
        })

        return
      }

      if (errorCode === 'INVALID_OR_EXPIRED') {
        showAlert({
          type: 'error',
          title: t(($) => $.errors.linkExpired),
          description: t(($) => $.errors.linkExpiredDescription),
        })

        return
      }

      showAlert({
        type: 'error',
        title: t(($) => $.errors.title),
        description: t(($) => $.errors.submissionFailed),
      })
    },
    [showAlert, t]
  )

  const handleFlowComplete = useCallback(
    async (data: Partial<FeedbackFlowData>) => {
      const parsedFlow = feedbackFlowDataSchema.safeParse(data)

      if (!parsedFlow.success) {
        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description: t(($) => $.errors.invalidAnswers),
        })
        return false
      }

      const answers = feedbackFlowDataToQuestionnaireAnswers(parsedFlow.data.feedbackAnswers)

      if (!answers || !hasRequiredQuestionnaireAnswers({ answers })) {
        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description: t(($) => $.errors.missingAnswers),
        })
        return false
      }

      const interestPayload = buildInterestPayload(parsedFlow.data)

      try {
        if (feedbackRequest) {
          if (!feedbackRequest.survey_nonce) {
            showAlert({
              type: 'error',
              title: t(($) => $.errors.title),
              description: t(($) => $.errors.invalidNonce),
            })
            return false
          }

          const payload: AddFeedbackPayload = {
            feedbackId: feedbackRequest.id,
            answers,
            nonce: feedbackRequest.survey_nonce,
            ...(interestPayload ? { interest: interestPayload } : {}),
          }

          const result = await submitFeedbackMutation.mutateAsync(payload)

          if (result && result.success) {
            onComplete('completed')
            return true
          }

          handleFeedbackRequestSubmissionError(result?.error || 'GENERAL_ERROR')
          return false
        }

        if (realtorUserId) {
          const fallbackContactDetails = parsedFlow.data.contactDetails ?? initialContactDetails
          const parsedContactDetails = realtorLinkContactSchema.safeParse(fallbackContactDetails)

          if (!parsedContactDetails.success) {
            showAlert({
              type: 'error',
              title: t(($) => $.errors.title),
              description: t(($) => $.realtorLink.missingContactDetails),
            })
            return false
          }

          const result = await submitRealtorLinkFeedbackMutation.mutateAsync({
            realtorUserId,
            contact: {
              name: parsedContactDetails.data.name,
              email: parsedContactDetails.data.email || undefined,
              phone: parsedContactDetails.data.phone || undefined,
              language: parsedContactDetails.data.language,
            },
            answers,
            ...(interestPayload ? { interest: interestPayload } : {}),
          })

          if (result.success) {
            onComplete('completed')
            return true
          }

          showAlert({
            type: 'error',
            title: t(($) => $.errors.title),
            description:
              result.error === 'REALTOR_NOT_FOUND'
                ? t(($) => $.realtorLink.realtorNotFound)
                : t(($) => $.errors.submissionFailed),
          })

          return false
        }

        const result = await submitBuyerSearchUpdateMutation.mutateAsync({
          answers,
          ...(interestPayload ? { interest: interestPayload } : {}),
        })

        if (result.success) {
          onComplete('completed')
          return true
        }

        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description:
            result.error === 'NO_PREVIOUS_FEEDBACK'
              ? t(($) => $.buyer.noPreviousFeedbackError)
              : t(($) => $.errors.submissionFailed),
        })

        return false
      } catch (error) {
        console.error('Feedback submission failed', error)
        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description: t(($) => $.errors.submissionFailedConnection),
        })
        return false
      }
    },
    [
      feedbackRequest,
      handleFeedbackRequestSubmissionError,
      initialContactDetails,
      onComplete,
      realtorUserId,
      showAlert,
      submitBuyerSearchUpdateMutation,
      submitFeedbackMutation,
      submitRealtorLinkFeedbackMutation,
      t,
    ]
  )

  if (requiresConsentAuth === null) {
    return <LoadingView />
  }

  return (
    <FlowProvider
      flowId={flowId}
      phases={phases}
      initialData={initialData}
      isNextDisabled={(data, currentPhaseId) =>
        isNextDisabled(data as Partial<FeedbackFlowData>, currentPhaseId)
      }
      isLoading={
        submitFeedbackMutation.isPending ||
        submitBuyerSearchUpdateMutation.isPending ||
        submitRealtorLinkFeedbackMutation.isPending ||
        setFeedbackInactiveMutation.isPending
      }
      onComplete={(data) => handleFlowComplete(data as Partial<FeedbackFlowData>)}
      onClose={(resetFlow) => {
        resetFlow()
        onClose()
      }}
      showCloseButton={false}
      showResetButton={false}
      flowTitle={
        flowTitle ?? t(($) => $.flow.title)
      }
      nextLabel={t(($) => $.flow.next)}
      completeLabel={t(($) => $.flow.complete)}
      onNext={async (
        flowData: Partial<FeedbackFlowData>,
        currentPhaseId,
        nextPhaseId,
        updateData
      ) => {
        if (nextPhaseId === 'consent' && currentPhaseId !== 'consent' && requiresConsentAuth) {
          try {
            await getOrCreateAnonymousUser()
          } catch (error) {
            console.error('Failed to create feedback session', error)
            showAlert({
              type: 'error',
              title: t(($) => $.errors.title),
              description: t(($) => $.errors.submissionFailed),
            })
            return false
          }
        }

        if (nextPhaseId === 'propertyInterest' && initialInterestData) {
          const flowInterest = flowData.propertyInterest
          const hasCurrentInterest =
            !!flowInterest &&
            (Object.keys(flowInterest.filters ?? {}).length > 0 ||
              (flowInterest.locations ?? []).length > 0)

          if (!hasCurrentInterest) {
            updateData('propertyInterest', {
              filters: initialInterestData.filters ?? {},
              locations: initialInterestData.locations ?? [],
            })
          }
        }

        return true
      }}>
      <FeedbackFlowContent
        realtorUserId={activeRealtorUserId}
        feedbackRequest={feedbackRequest}
        isSettingInactive={setFeedbackInactiveMutation.isPending}
        onSetInactive={handleSetInactive}
      />
    </FlowProvider>
  )
}

function FeedbackFlowContent({
  realtorUserId,
  feedbackRequest,
  isSettingInactive,
  onSetInactive,
}: {
  realtorUserId: string | null
  feedbackRequest?: FeedbackSurveyData
  isSettingInactive: boolean
  onSetInactive: () => Promise<void>
}) {
  const { t } = useTranslations('feedback')
  const { data, currentPhaseId, updateData } = useFlowManager()
  const saveProgressMutation = useSaveFeedbackProgress()

  const flowData = (data as Partial<FeedbackFlowData>) ?? {}

  const questionnaireData = useMemo(() => getFeedbackQuestionnaireData(t), [t])

  const currentQuestion = questionnaireData.questions.find(
    (question) => question.id === currentPhaseId
  )

  const handleUpdateAnswer = useCallback(
    async (questionId: QuestionnaireQuestionId, value: number | string) => {
      updateData('feedbackAnswers', (current) => {
        const currentAnswers = (current as FeedbackFlowData['feedbackAnswers']) ?? {}

        return {
          ...currentAnswers,
          [questionId]: value,
        }
      })

      if (!feedbackRequest?.id) {
        return
      }

      const shouldPersistProgress = !(questionId === 'timing' && value === 0)

      if (!shouldPersistProgress) {
        return
      }

      try {
        await getOrCreateAnonymousUser()
      } catch (error) {
        console.error('Failed to create anonymous feedback user', error)
      }

      try {
        await saveProgressMutation.mutateAsync({
          feedbackId: feedbackRequest.id,
          nonce: feedbackRequest.survey_nonce ?? null,
          answers: [{ id: questionId, value }],
        })
      } catch (error) {
        console.error('Failed to save feedback progress', error)
      }
    },
    [feedbackRequest, saveProgressMutation, updateData]
  )

  const currentTimingAnswer = flowData.feedbackAnswers?.timing

  const consentContact: ContactForConsent | null = feedbackRequest?.contact ?? null

  return (
    <>
      {currentQuestion && (
        <QuestionnairePageRenderer
          realtorId={realtorUserId}
          page={currentQuestion}
          answers={feedbackFlowDataToQuestionnaireAnswers(flowData.feedbackAnswers)}
          onUpdateAnswer={handleUpdateAnswer}
        />
      )}

      {currentPhaseId === 'timing' && currentTimingAnswer === 0 && (
        <View className="my-6 gap-4 pt-2">
          <InfoAlert level="info">
            {feedbackRequest
              ? t(($) => $.inactiveFlow.description)
              : t(($) => $.inactiveFlow.descriptionNoRequest)}
          </InfoAlert>
          {feedbackRequest && (
            <Button
              testID="feedback-inactive-button"
              variant="destructive"
              size="lg"
              onPress={() => {
                void onSetInactive()
              }}
              disabled={isSettingInactive}>
              <ButtonLabel>{t(($) => $.inactiveFlow.button)}</ButtonLabel>
            </Button>
          )}
        </View>
      )}

      {currentPhaseId === 'propertyInterest' && (
        <PropertyInterestQuestion
          interestData={flowData.propertyInterest}
          updateData={(interestData) => {
            updateData('propertyInterest', interestData)
          }}
        />
      )}

      {currentPhaseId === 'interestDescription' && (
        <PropertyInterestDescription
          flowData={flowData}
          onUpdateDescription={(description) => {
            updateData('interestDescription', (current) => ({
              ...current,
              description,
            }))
          }}
        />
      )}

      {(currentPhaseId === 'review' || currentPhaseId === 'consent') && (
        <FeedbackReviewPhase
          flowData={flowData}
          showConsentSheet={currentPhaseId === 'consent'}
          consentContact={consentContact}
          consentFeedbackId={feedbackRequest?.id ?? null}
          consentNonce={feedbackRequest?.survey_nonce ?? null}
        />
      )}
    </>
  )
}
